import React, { useEffect, useMemo, useState } from 'react';
import { ProtectedRoute } from '../../../components/ProtectedRoute';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { PromotionsFilter, PromotionsTable, AddPromotionModal, EditPromotionModal } from './components';
import { PromotionItem } from './promotionsTypes';
import { usePromotionsStore } from '../../../store/promotionsStore';
import { Promotion } from '../../../services/promotionsApi';
import styles from './AdminPromotions.module.css';

function formatRange(from: string, to: string): string {
  const f = new Date(from).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const t = new Date(to).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${f} – ${t}`;
}

function deriveStatus(p: Promotion): PromotionItem['status'] {
  if (!p.is_active) return 'Paused';
  const now = Date.now();
  if (new Date(p.ends_at).getTime() < now) return 'Expired';
  if (new Date(p.starts_at).getTime() > now) return 'Scheduled';
  return 'Active';
}

function adapt(p: Promotion): PromotionItem {
  return {
    id: String(p.id),
    name: p.name,
    type: p.discount_type === 'percent' ? '% Discount' : 'Bundle',
    conditions:
      p.scope === 'all'
        ? `${p.discount_value}${p.discount_type === 'percent' ? '%' : '$'} off all products`
        : p.scope === 'category'
          ? `${p.discount_value}${p.discount_type === 'percent' ? '%' : '$'} off ${p.category_name || 'category'}`
          : `${p.discount_value}${p.discount_type === 'percent' ? '%' : '$'} off ${p.sku_code || 'SKU'}`,
    tier: 'All',
    dateRange: formatRange(p.starts_at, p.ends_at),
    used: 0,
    status: deriveStatus(p),
  };
}

const AdminPromotions: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const items = usePromotionsStore((s) => s.items);
  const loading = usePromotionsStore((s) => s.loading);
  const error = usePromotionsStore((s) => s.error);
  const fetchList = usePromotionsStore((s) => s.fetchList);
  const update = usePromotionsStore((s) => s.update);
  const remove = usePromotionsStore((s) => s.remove);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Promotion | null>(null);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleEdit = (id: string) => {
    const p = items.find((x) => String(x.id) === id);
    if (p) setEditing(p);
  };

  const handleDelete = async (id: string) => {
    const p = items.find((x) => String(x.id) === id);
    if (!p) return;
    if (!window.confirm(`Delete promotion "${p.name}"? This cannot be undone.`)) return;
    try {
      await remove(p.id);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to delete promotion');
    }
  };

  const adapted = useMemo<PromotionItem[]>(() => items.map(adapt), [items]);

  const handleToggleActive = async (id: string, currentStatus: PromotionItem['status']) => {
    setBusyId(id);
    try {
      await update(Number(id), { is_active: currentStatus !== 'Active' });
    } catch (err) {
      // Surface via store error; nothing else to do here
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleFilter = (filter: string) => {
    setActiveFilter((prev) => (prev === filter ? null : filter));
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Active Promotions</h2>
          <p className={styles.subtitle}>Manage incoming stock operations — what needs to happen today</p>
        </div>
        <button className={styles.addBtn} onClick={() => setShowAddModal(true)}>
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
          New Promotions
        </button>
      </div>

      <PromotionsFilter activeFilter={activeFilter} onToggle={handleToggleFilter} />

      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && adapted.length === 0}
        emptyMessage="No promotions yet"
        onRetry={() => fetchList()}
      >
        <PromotionsTable
          items={adapted}
          onToggleActive={handleToggleActive}
          onEdit={handleEdit}
          onDelete={handleDelete}
          busyId={busyId}
        />
      </AsyncBoundary>

      <AddPromotionModal open={showAddModal} onClose={() => setShowAddModal(false)} />
      <EditPromotionModal open={!!editing} promotion={editing} onClose={() => setEditing(null)} />
    </div>
  );
};

export const AdminPromotionsPage: React.FC = () => (
  <ProtectedRoute requiredRoles={['admin']}>
    <AdminPromotions />
  </ProtectedRoute>
);

export { AdminPromotions };
export default AdminPromotions;
